import { useEffect, useState } from 'react';
import { useLocale, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useModalState } from 'hooks/useModalState/useModalState';
import { AppMessages } from 'i18n/messages';
import { downloadAttendanceTimeReport } from 'api/reports/reports';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { useAttendanceTimeReportValidation } from './AttendanceTimeReport.validation';
import { AttendanceTimeReportForm } from './AttendanceTimeReport.types';
import { AttendanceTimeReport } from './AttendanceTimeReport';

export const AttendanceTimeReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const { schema } = useAttendanceTimeReportValidation();
  const formMethods = useForm<AttendanceTimeReportForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { nazwisko, nrEwidencyjny, dataOd, dataDo, komorka } = formMethods.watch();

  const {
    onDownload: onDownloadAttendanceTimeReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadAttendanceTimeReport,
    buildRequestBody: (emails) => ({
      nazwisko,
      nrEwidencyjny,
      dataOd: dataOd?.toISOString(),
      dataDo: dataDo?.toISOString(),
      komorka: komorka?.id ? [komorka?.id] : [],
      selectedEmails: emails,
    }),
    onEmailModalClose,
    reportNameId: AppMessages['reports.attendanceTimeReport.title'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    // Save selected emails for persistence
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    // Trigger the download/send
    onDownloadAttendanceTimeReport(emails);
  };

  useEffect(() => {
    if (!isModalOpen) {
      // Reset form, error, and selected emails when modal closes
      formMethods.reset();
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <AttendanceTimeReport
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        onDownload={onDownloadAttendanceTimeReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.attendanceTimeReport.title'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
